import { useQuery } from 'react-query';
import { ShiftFilterOptions } from './components/ShiftFilter';
import moment from 'moment';
import apiClient from '../../apiClient';
import { JSX } from 'react';
import TyCalWeek from './components/CalWeek/TyCalWeek';
import CalendarItem from './components/CalendarItem';
import TyCalWeekLoading from './components/CalWeek/TyCalWeekLoading';
import TyCalWeekNoData from './components/CalWeek/TyCalWeekNoData';
import useSearchString from '../../hooks/UrlParamsHandler/useSearchParams';
import { CompanyData } from '@tymbe/schema/company.interface';
import { BranchofficeData } from '@tymbe/schema/branchoffice.interface';
import { ShiftData } from '../../types/TymbeApi';
import { ApplicationState } from '../../utils/enums';

type UseCalendarShiftsFilters = {
  templates?: number[];
  companyId?: number;
  branchofficeId?: number;
  departmentId?: number;
  shiftFilter?: string;
};

export const useCalendarShifts = (start: string, filters: UseCalendarShiftsFilters = {}) =>
  useQuery(
    ['shifts', 'calendar', start, filters],
    async () => {
      const modifiers: Record<string, boolean> = {
        withApplicationCounts: true,
      };
      if (filters.shiftFilter === ShiftFilterOptions.NOT_FULL) {
        modifiers.unFilledOnly = true;
      }
      if (filters.shiftFilter === ShiftFilterOptions.UNCONFIRMED_ATTENDANCE) {
        modifiers.unconfirmedAttendance = true;
      }

      const query = {
        start_time: {
          $gte: moment(start).startOf('week').toISOString(),
          $lte: moment(start).endOf('week').toISOString(),
        },
        'shift.company_id': filters.companyId,
        parent_id: filters.branchofficeId,
        branchoffice_id: filters.departmentId,
        $limit: -1,
        $modify: modifiers,
        $eager: '[application.attendance, perk, shiftTemplate]',
        $joinRelation: 'branchoffice',
        shift_template_id: {},
      };
      if (filters.templates?.length) {
        query.shift_template_id = { $in: filters.templates };
      }
      return apiClient.service('shift').find({ query });
    },
    { staleTime: 1000 * 5, keepPreviousData: true }
  );

function sortShiftByStartThenEnd(shifts: ShiftData[]): ShiftData[] {
  return shifts.sort(
    (a, b) =>
      moment(a.start_time).valueOf() - moment(b.start_time).valueOf() ||
      moment(a.end_time).valueOf() - moment(b.end_time).valueOf()
  );
}

const parseShiftsToTyCalWeekEvents = (shifts: ShiftData[]): JSX.Element[] => {
  return sortShiftByStartThenEnd(shifts).map((shift) => (
    <TyCalWeek.Event date={shift.start_time} data={shift} key={shift.id} className="relative">
      <CalendarItem
        data-date={shift.start_time}
        currentApplications={Number(shift.filled_orders_count) || 0}
        totalApplications={Number(shift.orders_count) || 0}
        hasUnconfirmedAttendance={
          !!shift.application?.some(
            ({ attendance, state }) => attendance === null && state === ApplicationState.CONFIRMED
          )
        }
        pendingInvitationsCount={
          shift.application?.filter((app) => app.invitation === true && app.state !== ApplicationState.CONFIRMED)
            .length || 0
        }
        emoji={shift.emoji || shift.shiftTemplate?.emoji}
        start={shift.start_time}
        end={shift.end_time}
        className="cursor-pointer"
        shiftId={shift.id}
      />
    </TyCalWeek.Event>
  ));
};

export const calendarContentBuilder = (
  shifts: ShiftData[],
  isLoading: boolean,
  isFetched: boolean
): JSX.Element | JSX.Element[] => {
  if (isLoading) {
    return <TyCalWeekLoading />;
  }

  if (!isLoading && isFetched && shifts?.length === 0) {
    return <TyCalWeekNoData />;
  }

  return parseShiftsToTyCalWeekEvents(shifts);
};

export const useUrlParameters = () => {
  const [searchParams] = useSearchString();

  const companyIdFromURL = Number(searchParams.get('company_id')) || undefined;
  const branchofficeIdFromURL = Number(searchParams.get('branchoffice_id')) || undefined;
  const departmentIdFromURL = Number(searchParams.get('department_id')) || undefined;

  return [companyIdFromURL, branchofficeIdFromURL, departmentIdFromURL];
};

export const getDefaultOption = (options: Array<CompanyData | BranchofficeData>): number | undefined => {
  return options.length === 1 ? options[0].id : undefined;
};
